/**
 * Storage Fallback Mechanism
 *
 * Provides localStorage fallback when IndexedDB fails in certain browser contexts
 * (private browsing, iframe restrictions, etc.)
 */

import type { StoredEncryptedAsset, AssetStorageMetadata } from './EncryptedAssetStorage';

const STORAGE_PREFIX = 'quantum-safe-vault';
const ASSETS_KEY = `${STORAGE_PREFIX}-assets`;
const METADATA_KEY = `${STORAGE_PREFIX}-metadata`;

export class LocalStorageFallback {
  private isAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  async storeEncryptedAsset(
    safeAddress: string,
    assetId: string,
    asset: StoredEncryptedAsset,
    metadata: AssetStorageMetadata
  ): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('LocalStorage not available');
    }

    console.log('LocalStorageFallback: Storing encrypted asset', { safeAddress, assetId });

    try {
      // Get existing data
      const existingAssets = this.getAllAssets();
      const existingMetadata = this.getAllMetadata();

      // Add new asset
      const assetKey = `${safeAddress}:${assetId}`;
      existingAssets[assetKey] = asset;
      existingMetadata[assetKey] = metadata;

      // Store back
      localStorage.setItem(ASSETS_KEY, JSON.stringify(existingAssets));
      localStorage.setItem(METADATA_KEY, JSON.stringify(existingMetadata));

      console.log('LocalStorageFallback: Successfully stored asset');
    } catch (error) {
      console.error('LocalStorageFallback: Storage failed', error);
      throw new Error(`LocalStorage storage failed: ${error}`);
    }
  }

  async getEncryptedAsset(safeAddress: string, assetId: string): Promise<StoredEncryptedAsset | null> {
    if (!this.isAvailable()) return null;

    const assets = this.getAllAssets();
    const assetKey = `${safeAddress}:${assetId}`;
    return assets[assetKey] || null;
  }

  async getEncryptedAssetsForSafe(safeAddress: string): Promise<AssetStorageMetadata[]> {
    if (!this.isAvailable()) return [];

    const metadata = this.getAllMetadata();
    return Object.entries(metadata)
      .filter(([key]) => key.startsWith(`${safeAddress}:`))
      .map(([_, meta]) => meta);
  }

  async removeEncryptedAsset(safeAddress: string, assetId: string): Promise<void> {
    if (!this.isAvailable()) return;

    console.log('LocalStorageFallback: Removing encrypted asset', { safeAddress, assetId });

    const assets = this.getAllAssets();
    const metadata = this.getAllMetadata();
    const assetKey = `${safeAddress}:${assetId}`;

    delete assets[assetKey];
    delete metadata[assetKey];

    localStorage.setItem(ASSETS_KEY, JSON.stringify(assets));
    localStorage.setItem(METADATA_KEY, JSON.stringify(metadata));
  }

  async getStorageStats(): Promise<{
    totalAssets: number;
    totalSize: number;
    safesWithAssets: string[];
  }> {
    if (!this.isAvailable()) {
      return { totalAssets: 0, totalSize: 0, safesWithAssets: [] };
    }

    const assets = this.getAllAssets();
    const safes = new Set<string>();
    let totalSize = 0;

    Object.entries(assets).forEach(([key, asset]) => {
      const [safeAddress] = key.split(':');
      safes.add(safeAddress);
      totalSize += JSON.stringify(asset).length;
    });

    return {
      totalAssets: Object.keys(assets).length,
      totalSize,
      safesWithAssets: Array.from(safes)
    };
  }

  async clearAllAssets(): Promise<void> {
    if (!this.isAvailable()) return;

    localStorage.removeItem(ASSETS_KEY);
    localStorage.removeItem(METADATA_KEY);
    console.log('LocalStorageFallback: Cleared all assets');
  }

  private getAllAssets(): Record<string, StoredEncryptedAsset> {
    try {
      const data = localStorage.getItem(ASSETS_KEY);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  getAllMetadata(): Record<string, AssetStorageMetadata> {
    try {
      const data = localStorage.getItem(METADATA_KEY);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  debugContents(): void {
    console.log('=== LocalStorageFallback DEBUG ===');
    console.log('Available:', this.isAvailable());
    console.log('Assets:', this.getAllAssets());
    console.log('Metadata:', this.getAllMetadata());
    console.log('=== END DEBUG ===');
  }
}

export default LocalStorageFallback;